import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { teamInfo } from "../../data/arsenalData";

const NAV_ITEMS = [
  { label: "Inicio", href: "#hero" },
  { label: "Historia", href: "#historia" },
  { label: "Competición", href: "#competicion" },
  { label: "Match Day", href: "#matchday" },
  { label: "Estadísticas", href: "#stats" },
  { label: "Multimedia", href: "#multimedia" },
];

/** Ancho mínimo para nav horizontal (evita depender solo de clases md: de Tailwind en el build). */
const DESKTOP_NAV_MIN_PX = 1024;

function useIsDesktopNav() {
  const [wide, setWide] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(min-width: ${DESKTOP_NAV_MIN_PX}px)`).matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${DESKTOP_NAV_MIN_PX}px)`);
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return wide;
}

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isDesktopNav = useIsDesktopNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (isDesktopNav) setOpen(false);
  }, [isDesktopNav]);

  return (
    <motion.header
      data-testid="main-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-[100] transition-all duration-500 ${
        scrolled ? "glass-dark py-2 shadow-lg shadow-black/20" : "bg-[#061930]/85 backdrop-blur-md py-3 md:py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <a href="#hero" data-testid="header-logo-link" className="flex items-center gap-3 group">
          <img
            src={teamInfo.logo}
            alt="Arsenal de Allende"
            className="w-12 h-12 object-contain transition-transform duration-500 group-hover:scale-110 group-hover:rotate-[-6deg]"
          />
          <div className="hidden sm:block">
            <div className="font-anton text-lg uppercase tracking-wider leading-none text-white">
              Arsenal
            </div>
            <div className="font-outfit text-[10px] uppercase tracking-[0.3em] text-[#9C824A]">
              de Allende
            </div>
          </div>
        </a>

        {isDesktopNav ? (
          <nav className="flex items-center gap-1" aria-label="Principal">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className="relative px-4 py-2 font-outfit text-sm font-medium uppercase tracking-wider text-white/80 hover:text-white transition-colors group"
              >
                {item.label}
                <span className="absolute bottom-1 left-4 right-4 h-[2px] bg-[#DB0007] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
              </a>
            ))}
          </nav>
        ) : null}

        <div className="flex items-center gap-3 shrink-0">
          {isDesktopNav && (
            <a
              href="#matchday"
              data-testid="header-cta-matchday"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#DB0007] text-white font-anton uppercase text-sm tracking-widest hover:bg-[#9C824A] transition-colors"
            >
              <span className="w-2 h-2 bg-white rounded-full live-pulse" />
              Match Day
            </a>
          )}
          {!isDesktopNav && (
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              aria-controls="mobile-menu-panel"
              className="w-11 h-11 flex items-center justify-center text-white bg-white/5 border border-white/10 hover:bg-white/10"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile menu */}
      {open && !isDesktopNav && (
        <motion.div
          id="mobile-menu-panel"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-dark mx-4 mt-2 p-4"
          data-testid="mobile-menu"
        >
          {NAV_ITEMS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setOpen(false)}
              className="block py-3 px-3 font-outfit text-white/90 uppercase tracking-wide text-sm border-b border-white/5 last:border-0 hover:text-[#DB0007]"
            >
              {item.label}
            </a>
          ))}
        </motion.div>
      )}
    </motion.header>
  );
};
